import { useEffect, useRef, useSyncExternalStore } from 'react';

/**
 * Fase 2 (W3, F1a): uma conexão SSE por aba (`/api/v1/tempo-real`). O servidor manda só o sinal (tipo e ids), nunca o
 * dado: quem ouve invalida a consulta certa. Sem conexão (Redis fora, 503, rede), `conectado` fica falso e as telas
 * voltam ao polling; a reconexão tenta de novo em 30 s, dobrando a cada queda seguida até 5 min (W16), e só com a aba à vista. O servidor fecha a conexão a cada 5 min para reconferir a
 * permissão, e o navegador reabre sozinho.
 */
export const SINAIS = ['notificacao', 'conversas', 'tarefas', 'equipe', 'agenda'] as const;
export type Sinal = (typeof SINAIS)[number];
type Ouvinte = (tipo: Sinal, dados: Record<string, unknown>) => void;

const ouvintes = new Set<Ouvinte>();
const assinantesEstado = new Set<() => void>();
let fonte: EventSource | null = null;
let conectado = false;
let religar: ReturnType<typeof setTimeout> | null = null;
/** Fase 2 (W16): quedas seguidas (429 do teto de conexões, 503 sem Redis, rede): a espera dobra até 5 min. */
let quedas = 0;
let esperandoAba: (() => void) | null = null;

/** Espera antes de reabrir o SSE depois de `n` quedas seguidas: 30 s, 60 s, 120 s... até 5 min. */
export const esperaReconexao = (n: number) => Math.min(300_000, 30_000 * 2 ** Math.max(0, n - 1));

/** Reabre depois da espera; com a aba oculta, só quando ela voltar (nada de conexão nova para quem não está olhando). */
function agendarReabertura() {
  religar = setTimeout(() => {
    religar = null;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      const volta = () => {
        if (document.visibilityState === 'hidden') return;
        document.removeEventListener('visibilitychange', volta);
        esperandoAba = null;
        if (!fonte) abrir();
      };
      esperandoAba = volta;
      document.addEventListener('visibilitychange', volta);
      return;
    }
    abrir();
  }, esperaReconexao(quedas));
}

function definirConectado(v: boolean) {
  if (v === conectado) return;
  conectado = v;
  assinantesEstado.forEach((f) => f());
}

function abrir() {
  if (typeof EventSource === 'undefined') return;
  const f = new EventSource('/api/v1/tempo-real');
  fonte = f;
  f.addEventListener('pronto', () => {
    quedas = 0;
    definirConectado(true);
  });
  for (const tipo of SINAIS) {
    f.addEventListener(tipo, (e) => {
      let dados: Record<string, unknown> = {};
      try {
        dados = JSON.parse((e as MessageEvent<string>).data) as Record<string, unknown>;
      } catch {
        /* sinal sem dados */
      }
      ouvintes.forEach((o) => o(tipo, dados));
    });
  }
  f.onerror = () => {
    definirConectado(false);
    // Fechada de vez (503, 401, 429): o navegador não tenta sozinho; tentamos de novo mais tarde.
    if (f.readyState === EventSource.CLOSED && fonte === f) {
      fonte = null;
      quedas += 1;
      agendarReabertura();
    }
  };
}

export function iniciarTempoReal() {
  if (!fonte && !religar && !esperandoAba) abrir();
}

export function pararTempoReal() {
  if (religar) clearTimeout(religar);
  religar = null;
  if (esperandoAba && typeof document !== 'undefined') document.removeEventListener('visibilitychange', esperandoAba);
  esperandoAba = null;
  quedas = 0;
  fonte?.close();
  fonte = null;
  definirConectado(false);
}

export const tempoRealConectado = () => conectado;

/** Verdadeiro com o SSE de pé: a tela pode desligar o polling. */
export function useTempoRealConectado() {
  return useSyncExternalStore(
    (f) => {
      assinantesEstado.add(f);
      return () => assinantesEstado.delete(f);
    },
    () => conectado,
    () => false,
  );
}

/** Chama `fn` a cada sinal do tipo (a função mais recente, sem reassinar). */
export function useSinal(tipo: Sinal, fn: (dados: Record<string, unknown>) => void) {
  const ref = useRef(fn);
  useEffect(() => {
    ref.current = fn;
  });
  useEffect(() => {
    const o: Ouvinte = (t, d) => t === tipo && ref.current(d);
    ouvintes.add(o);
    return () => void ouvintes.delete(o);
  }, [tipo]);
}

/** Liga o SSE enquanto há sessão; troca de workspace reabre (o canal é do workspace). */
export function useConexaoTempoReal(workspaceId: string) {
  useEffect(() => {
    iniciarTempoReal();
    return pararTempoReal;
  }, [workspaceId]);
}
